import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { PlayIcon as Play, StopIcon as Stop, WarningIcon as Warning, FootprintsIcon as Footprints, ShieldCheckIcon as ShieldCheck, CameraIcon as Camera } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import PassportSafetyFlags from "@/components/PassportSafetyFlags";

/**
 * Walker-side live-tracking control (in-app only).
 *
 * Walker taps "Start walk", grants geolocation permission, and the
 * component streams GPS points to the backend every ~15s. The owner
 * sees the dot inside their authenticated /my-bookings — there is
 * NO public no-auth share link, by design.
 *
 *   1. POST /api/bookings/{id}/walk/start  → opens the session
 *   2. navigator.geolocation.watchPosition (high-accuracy, 15s
 *      throttle) → POSTs every fix to /walks/{walk_id}/point
 *   3. "End walk" stops watchPosition + POSTs /end, which collapses
 *      raw points to a summary and discards the route from the DB
 *
 * Backgrounding: phones suspend watchPosition aggressively when the
 * tab is backgrounded. We surface a clear banner so the walker keeps
 * the screen on. We intentionally do NOT request a Wake Lock — staying
 * a venue means we don't write code that nags the walker's device
 * into operating like an employer-issued tracker.
 */
const POST_INTERVAL_MS = 15_000;
const HIGH_ACCURACY = true;

export default function WalkerLiveTracking({ booking }) {
  const [walkId, setWalkId] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | requesting | active | ended | error
  const [pointCount, setPointCount] = useState(0);
  const [lastError, setLastError] = useState(null);

  const watchIdRef = useRef(null);
  const lastPostAtRef = useRef(0);
  const inFlightRef = useRef(false);
  const [bgWarning, setBgWarning] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoCount, setPhotoCount] = useState(0);
  const photoInputRef = useRef(null);

  const sendPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !walkId) return;
    setPhotoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = up.data?.url || up.data?.public_url || up.data?.storage_path;
      const r = await api.post(`/walks/${walkId}/photo`, { url });
      setPhotoCount(r.data?.photo_count || photoCount + 1);
      toast.success("Photo sent — it's on the owner's lock screen right now 📸");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send the photo — try again.");
    } finally {
      setPhotoBusy(false);
    }
  };

  useEffect(() => {
    return () => {
      if (watchIdRef.current !== null && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (status !== "active") {
      setBgWarning(false);
      return undefined;
    }
    const onVis = () => setBgWarning(document.visibilityState === "hidden");
    document.addEventListener("visibilitychange", onVis);
    onVis();
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [status]);

  // Resume an in-flight walk on tab refresh (don't double-start).
  useEffect(() => {
    if (!booking?.id) return;
    let cancelled = false;
    api
      .get(`/bookings/${booking.id}/walk`)
      .then(({ data }) => {
        if (cancelled || !data?.walk) return;
        if (data.walk.status === "active" && data.walk.walk_id) {
          setWalkId(data.walk.walk_id);
          setStatus("active");
          setPointCount(data.walk.point_count || 0);
          startWatching(data.walk.walk_id);
        }
      })
      .catch(() => { /* non-critical */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking?.id]);

  const postPoint = async (id, lat, lng, accuracy_m) => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const { data } = await api.post(`/walks/${id}/point`, {
        lat, lng, accuracy_m,
        ts: new Date().toISOString(),
      });
      if (typeof data?.point_count === "number") setPointCount(data.point_count);
      setLastError(null);
    } catch (err) {
      // Owner may have flipped consent off mid-walk → 409.
      if (err?.response?.status === 409) {
        toast.error("Owner ended live sharing. Tracking stopped on your device.");
        if (watchIdRef.current !== null && navigator.geolocation) {
          navigator.geolocation.clearWatch(watchIdRef.current);
          watchIdRef.current = null;
        }
        setStatus("ended");
      } else {
        setLastError("Network blip — retrying with the next fix");
      }
    } finally {
      inFlightRef.current = false;
    }
  };

  const startWatching = (id) => {
    if (!navigator.geolocation) {
      toast.error("Your browser doesn't support GPS tracking.");
      setStatus("error");
      return;
    }
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const now = Date.now();
        if (now - lastPostAtRef.current < POST_INTERVAL_MS) return;
        lastPostAtRef.current = now;
        const { latitude, longitude, accuracy } = pos.coords;
        postPoint(id, latitude, longitude, accuracy);
      },
      (err) => {
        let msg = "GPS error";
        if (err.code === 1) msg = "Location permission was denied.";
        else if (err.code === 2) msg = "Location unavailable. Try moving outdoors.";
        else if (err.code === 3) msg = "GPS timed out. Will keep trying.";
        setLastError(msg);
        if (err.code === 1) {
          setStatus("error");
          if (watchIdRef.current !== null) {
            navigator.geolocation.clearWatch(watchIdRef.current);
            watchIdRef.current = null;
          }
        }
      },
      { enableHighAccuracy: HIGH_ACCURACY, maximumAge: 5_000, timeout: 30_000 },
    );
  };

  const startWalk = async () => {
    setStatus("requesting");
    setLastError(null);
    try {
      const { data } = await api.post(`/bookings/${booking.id}/walk/start`, {
        booking_id: booking.id,
      });
      setWalkId(data.walk_id);
      setStatus("active");
      setPointCount(0);
      startWatching(data.walk_id);
      toast.success(
        data.already_active
          ? "Resumed existing walk"
          : "Walk started — the owner can see your location in their My Bookings.",
      );
    } catch (err) {
      setStatus("idle");
      const msg = err?.response?.data?.detail;
      const httpStatus = err?.response?.status;
      if (httpStatus === 403 && typeof msg === "string" && msg.includes("hasn't enabled live walk")) {
        setLastError(
          "The owner hasn't enabled live walk tracking on this booking yet. Ask them to turn it on from their My Bookings, then tap Start again.",
        );
        setStatus("error");
      } else {
        toast.error(typeof msg === "string" ? msg : "Couldn't start the walk — try again?");
      }
    }
  };

  const revokeShare = async () => {
    if (!walkId) return;
    if (!window.confirm(
      "Stop sharing the live walk right now? This ends the walk; a recap stays visible to the owner for 24 hours, then is permanently deleted.",
    )) {
      return;
    }
    if (watchIdRef.current !== null && navigator.geolocation) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    try {
      await api.post(`/walks/${walkId}/revoke`, {});
      setStatus("ended");
      toast.success("Sharing stopped.");
    } catch (err) {
      toast.error("Couldn't revoke right now — tracking has stopped on your device.");
      setStatus("ended");
    }
  };

  const endWalk = async () => {
    if (!walkId) return;
    if (watchIdRef.current !== null && navigator.geolocation) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    try {
      const finalPoint = await new Promise((resolve) => {
        if (!navigator.geolocation) return resolve(null);
        navigator.geolocation.getCurrentPosition(
          (pos) => resolve({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy_m: pos.coords.accuracy,
            ts: new Date().toISOString(),
          }),
          () => resolve(null),
          { timeout: 5000 },
        );
      });
      await api.post(`/walks/${walkId}/end`, { final_point: finalPoint });
      setStatus("ended");
      toast.success("Walk ended. The owner can see the recap for 24 hours.");
    } catch (err) {
      toast.error("Couldn't end the walk cleanly — but tracking has stopped on your device.");
      setStatus("ended");
    }
  };

  // -----------------------------------------------------------------
  // Renders
  // -----------------------------------------------------------------
  const VenueDisclaimer = () => (
    <div
      className="mt-3 rounded-md border border-[#E2DAC2] bg-cream/40 px-3 py-2 text-[11.5px] leading-snug text-[#6B6B6B] flex items-start gap-2"
      data-testid="walker-venue-disclaimer"
    >
      <ShieldCheck size={13} weight="duotone" className="text-gold-dark flex-shrink-0 mt-0.5" />
      <span>
        Armstrong K9 Registry transmits your location to the owner during
        active walks only. <strong>Armstrong does not monitor your walk</strong>{" "}
        and never stores your full route after the walk ends — only a
        24-hour recap (distance, duration, snapped start/end pins). You
        control when sharing starts and can stop it at any time.
      </span>
    </div>
  );

  if (status === "idle") {
    return (
      <div
        className="rounded-xl border border-[#E2DAC2] bg-white p-5"
        data-testid="walker-live-tracking-idle"
      >
        <div className="flex items-center gap-3 mb-3">
          <Footprints size={22} weight="duotone" className="text-gold" />
          <div>
            <p className="font-spectral text-lg text-ink leading-tight">Live walk tracking</p>
            <p className="text-[12.5px] text-[#6B6B6B] mt-0.5">
              Share live location with the owner while you&rsquo;re out. Owner-controlled consent · in-app only · no public link.
            </p>
          </div>
        </div>
        <Button
          onClick={startWalk}
          className="rounded-full bg-ink hover:bg-crimson text-white h-11 px-6 font-semibold"
          data-testid="walker-start-walk"
        >
          <Play size={15} weight="fill" className="mr-2" />
          Start walk
        </Button>
        <VenueDisclaimer />
      </div>
    );
  }

  if (status === "requesting") {
    return (
      <div className="rounded-xl border border-[#E2DAC2] bg-white p-5 text-center" data-testid="walker-live-tracking-requesting">
        <Footprints size={28} weight="duotone" className="text-gold mx-auto mb-2 animate-pulse" />
        <p className="text-[14px] text-ink">Waiting for GPS permission…</p>
        <p className="text-[12px] text-[#6B6B6B] mt-1">
          Allow location access so the owner can see where you&rsquo;re walking.
        </p>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div
        className="rounded-xl border border-crimson/30 bg-crimson/5 p-5"
        data-testid="walker-live-tracking-error"
      >
        <div className="flex items-center gap-2 mb-2">
          <Warning size={20} weight="fill" className="text-crimson" />
          <p className="font-bold text-ink text-[14px]">Can&rsquo;t start tracking</p>
        </div>
        <p className="text-[13px] text-[#3F3F3F] leading-relaxed">{lastError}</p>
        <Button
          variant="outline"
          onClick={() => { setStatus("idle"); setLastError(null); }}
          className="mt-3 rounded-full h-9 px-4 text-[12px]"
        >
          Try again
        </Button>
      </div>
    );
  }

  // active or ended
  return (
    <div
      className="rounded-xl border border-gold/40 bg-gradient-to-br from-white to-cream/50 p-5"
      data-testid={`walker-live-tracking-${status}`}
    >
      {booking?.dog_id && <PassportSafetyFlags dogId={booking.dog_id} />}
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Footprints size={22} weight="duotone" className="text-gold" />
          <div>
            <p className="font-spectral text-lg text-ink leading-tight">
              {status === "active" ? "Walk in progress" : "Walk completed"}
            </p>
            <p className="text-[12px] text-[#6B6B6B] mt-0.5">
              {pointCount} GPS reading{pointCount === 1 ? "" : "s"}
              {status === "active"
                ? " · sharing live with the owner in-app"
                : " · recap visible to owner for 24 hours"}
            </p>
          </div>
        </div>
        {status === "active" && (
          <span className="inline-flex items-center gap-2 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-300 px-3 py-1 text-[11px] font-bold uppercase tracking-wide">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-80 animate-ping" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
            </span>
            Live
          </span>
        )}
      </div>

      {lastError && status === "active" && (
        <p className="text-[12px] text-amber-700 mb-3">{lastError}</p>
      )}

      {bgWarning && status === "active" && (
        <p
          className="text-[12px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-3"
          data-testid="walker-bg-warning"
        >
          ⚠ This tab is in the background. GPS may pause until you bring it back.
          Keep your screen on for the cleanest map.
        </p>
      )}

      {status === "active" && (
        <div className="mb-3">
          <input
            ref={photoInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={sendPhoto}
            className="hidden"
            data-testid="walker-photo-input"
          />
          <Button
            onClick={() => photoInputRef.current?.click()}
            disabled={photoBusy || photoCount >= 5}
            className="rounded-full h-10 px-5 text-[13px] bg-[#0E0E0E] text-white hover:bg-[#C1272D] w-full sm:w-auto"
            data-testid="walker-send-photo"
          >
            <Camera size={15} weight="duotone" className="mr-2" />
            {photoBusy ? "Sending…" : photoCount >= 5 ? "Photo limit reached (5)" : "Send photo to owner"}
          </Button>
          <p className="text-[11px] text-[#6B6B6B] mt-1.5">
            Snap the dog mid-walk — the owner gets it on their lock screen instantly.
            {photoCount > 0 ? ` Sent ${photoCount}/5 this walk.` : " Up to 5 per walk."}
          </p>
        </div>
      )}

      {status === "active" && (
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={endWalk}
            variant="outline"
            className="rounded-full h-10 px-5 text-[13px] border-ink/30 text-ink hover:bg-ink hover:text-white hover:border-ink"
            data-testid="walker-end-walk"
          >
            <Stop size={14} weight="fill" className="mr-2" />
            End walk (saves recap)
          </Button>
          <Button
            onClick={revokeShare}
            variant="outline"
            className="rounded-full h-10 px-5 text-[13px] border-crimson/40 text-crimson hover:bg-crimson hover:text-white hover:border-crimson"
            data-testid="walker-revoke-walk"
          >
            Stop sharing now
          </Button>
        </div>
      )}
      <VenueDisclaimer />
    </div>
  );
}
